import React, { useState, useEffect, useRef } from 'react';
import { Room, createLocalAudioTrack, Track } from 'livekit-client';
import './DailyReview.css';
import maisyLogo from './assets/maisy-logo.png';
import maisyBot from './assets/maisy-image.png';

const DailyReview = () => {
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [room, setRoom] = useState(null);
  const [audioBlocked, setAudioBlocked] = useState(false);

  // Audio elements for the agent's tracks, kept out of React state so cleanup is reliable
  const audioElementsRef = useRef([]);

  // Silently read from URL parameters passed by mobile app developer
  const [encryptedKey, setEncryptedKey] = useState('');
  const [empId, setEmpId] = useState('');
  const [date, setDate] = useState('');

  // Daily summary metrics fetched directly from backend
  const [infields, setInfields] = useState(null);
  const [metaInfo, setMetaInfo] = useState({ empId: null, cmpId: null, date: null, userName: null });

  // Currency is resolved by the backend (deep-link token > analytics summary > INR fallback)
  const [currency, setCurrency] = useState({ code: 'INR', symbol: '\u20b9', label: 'INR (\u20b9)', locale: 'en-IN' });

  const applyCurrency = (data) => {
    const code = data.currency || data.infields?.currency;
    if (!code) return;
    const symbol = data.currency_symbol || data.infields?.currency_symbol || code;
    setCurrency({
      code,
      symbol,
      label: data.currency_label || data.infields?.currency_label || `${code} (${symbol})`,
      locale: data.currency_locale || data.infields?.currency_locale || 'en-US'
    });
  };
  const [loadingSummary, setLoadingSummary] = useState(false);

  const fetchSummaryOnLoad = async (key, emp, targetDate) => {
    if (!key && !emp) return;
    setLoadingSummary(true);
    try {
      const serverUrl = process.env.REACT_APP_TOKEN_SERVER_URL_DAILY_REVIEW 
        ? process.env.REACT_APP_TOKEN_SERVER_URL_DAILY_REVIEW.replace('/token/daily-review', '/daily-review-summary') 
        : (process.env.REACT_APP_BACKEND_SERVER 
            ? `${process.env.REACT_APP_BACKEND_SERVER}/api/daily-review-summary?` 
            : 'https://massistcrm.ai/api/daily-review-summary?');

      const urlParams = new URLSearchParams();
      if (key) urlParams.append('encrypted_key', key);
      if (emp) urlParams.append('emp_id', emp);
      if (targetDate) urlParams.append('date', targetDate);
      const currencyOverride = new URLSearchParams(window.location.search).get('currency');
      if (currencyOverride) urlParams.append('currency', currencyOverride);

      const fullUrl = `${serverUrl.endsWith('?') ? serverUrl : serverUrl + '?'}${urlParams.toString()}`;
      const resp = await fetch(fullUrl);
      if (resp.ok) {
        const data = await resp.json();
        if (data.infields) setInfields(data.infields);
        applyCurrency(data);
        setMetaInfo({
          empId: data.emp_id || data.infields?.emp_id || emp || 'N/A',
          cmpId: data.cmp_id || data.infields?.cmp_id || '627',
          date: data.date || data.infields?.date || targetDate || new Date().toISOString().split('T')[0],
          userName: data.user_name || data.infields?.user_name || null
        });
      }
    } catch (err) {
      console.error('Error auto-fetching summary analytics on page load:', err);
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    // Automatically capture encrypted token passed in frontend URL without asking the user
    const params = new URLSearchParams(window.location.search);
    let keyFromUrl = params.get('encrypted_key') || params.get('encryted_key') || params.get('EncryptedData') || params.get('token') || '';
    
    // Support raw tokens passed directly after '?' without a parameter name (e.g. /daily-review?21vWgPtu...)
    if (!keyFromUrl && window.location.search.length > 1) {
      const rawQuery = decodeURIComponent(window.location.search.substring(1).trim());
      if (rawQuery.startsWith('encrypted_key=')) {
        keyFromUrl = rawQuery.substring('encrypted_key='.length);
      } else if (rawQuery.startsWith('encryted_key=')) {
        keyFromUrl = rawQuery.substring('encryted_key='.length);
      } else {
        keyFromUrl = rawQuery;
      }
    }

    const empFromUrl = params.get('emp_id') || '';
    const dateFromUrl = params.get('date') || '';

    if (keyFromUrl) setEncryptedKey(keyFromUrl);
    if (empFromUrl) setEmpId(empFromUrl);
    if (dateFromUrl) setDate(dateFromUrl);

    if (keyFromUrl || empFromUrl) {
      fetchSummaryOnLoad(keyFromUrl, empFromUrl, dateFromUrl);
    }
  }, []);

  // Attach an agent audio track to a hidden <audio> element and start playback
  const attachAudioTrack = (track) => {
    if (!track || track.kind !== Track.Kind.Audio) return;

    const audioElement = track.attach();
    audioElement.autoplay = true;
    audioElement.playsInline = true;
    audioElement.muted = false;
    audioElement.volume = 1.0;
    audioElement.style.display = 'none';
    document.body.appendChild(audioElement);
    audioElementsRef.current.push(audioElement);

    const playPromise = audioElement.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        console.warn('Agent audio autoplay blocked by browser:', err);
        setAudioBlocked(true);
      });
    }
  };

  const removeAudioElements = () => {
    audioElementsRef.current.forEach((el) => el.remove());
    audioElementsRef.current = [];
  };

  // Clean up any attached audio on unmount
  useEffect(() => removeAudioElements, []);

  // Browser refused autoplay — one tap on the call screen resumes it
  const enableAudioPlayback = async () => {
    if (!room) return;
    try {
      await room.startAudio();
      audioElementsRef.current.forEach((el) => {
        const p = el.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      });
      setAudioBlocked(false);
    } catch (err) {
      console.error('Unable to start audio playback:', err);
    }
  };

  const handleStartReview = () => {
    if (window.AndroidBridge && window.AndroidBridge.requestMicPermission) {
      window.onMicPermissionResult = (isGranted) => {
        if (isGranted) {
          connectToRoom();
        } else {
          setError("Microphone permission was denied.");
        }
      };
      window.AndroidBridge.requestMicPermission();
    } else {
      connectToRoom();
    }
  };

  const connectToRoom = async () => {
    try {
      setConnecting(true);
      setError(null);
      setAudioBlocked(false);

      const serverUrl = process.env.REACT_APP_TOKEN_SERVER_URL_DAILY_REVIEW || (
        process.env.REACT_APP_BACKEND_SERVER 
          ? `${process.env.REACT_APP_BACKEND_SERVER}/api/token/daily-review?` 
          : 'https://massistcrm.ai/api/token/daily-review?'
      );

      const roomName = `dr-room-${Math.random().toString(36).substring(2, 8)}`;
      const userName = `emp-${Math.random().toString(36).substring(2, 6)}`;

      let tokenUrl = `${serverUrl.endsWith('?') ? serverUrl : serverUrl + '?'}&room=${encodeURIComponent(roomName)}&user=${encodeURIComponent(userName)}&language=English`;

      if (encryptedKey) {
        tokenUrl += `&encrypted_key=${encodeURIComponent(encryptedKey)}`;
      } else {
        if (empId) tokenUrl += `&emp_id=${encodeURIComponent(empId)}`;
        if (date) tokenUrl += `&date=${encodeURIComponent(date)}`;
      }

      const currencyOverride = new URLSearchParams(window.location.search).get('currency');
      if (currencyOverride) tokenUrl += `&currency=${encodeURIComponent(currencyOverride)}`;

      console.log('Connecting to Daily Review server:', tokenUrl);
      const response = await fetch(tokenUrl);

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to fetch LiveKit token (${response.status}): ${errText}`);
      }

      const data = await response.json();
      const token = data.token;
      const wssUrl = data.wss_url || process.env.REACT_APP_LIVEKIT_WS_URL_DAILY_REVIEW || 'wss://massistcrm.ai:9000/ws';

      if (data.infields) setInfields(data.infields);
      applyCurrency(data);
      if (data.emp_id || data.date || data.cmp_id || data.user_name) {
        setMetaInfo((prev) => ({
          empId: data.emp_id || empId || prev.empId || 'N/A',
          cmpId: data.cmp_id || prev.cmpId || '627',
          date: data.date || date || prev.date || 'N/A',
          userName: data.user_name || data.infields?.user_name || prev.userName || null
        }));
      }

      const newRoom = new Room();

      // Register listeners BEFORE connecting: the agent greets as soon as it joins,
      // so a listener attached after connect (or after the mic prompt) misses the track.
      newRoom.on('trackSubscribed', (track) => attachAudioTrack(track));

      newRoom.on('trackUnsubscribed', (track) => {
        if (track.kind === Track.Kind.Audio) {
          track.detach().forEach((element) => element.remove());
          audioElementsRef.current = audioElementsRef.current.filter((el) => el.isConnected);
          setIsSpeaking(false);
        }
      });

      newRoom.on('activeSpeakersChanged', (speakers) => {
        const agentSpeaking = speakers.some(s => s.identity !== newRoom.localParticipant?.identity);
        setIsSpeaking(agentSpeaking);
      });

      newRoom.on('audioPlaybackChanged', () => {
        setAudioBlocked(!newRoom.canPlaybackAudio);
      });

      await newRoom.connect(wssUrl, token);

      // Unlock playback while still inside the user's tap gesture
      try {
        await newRoom.startAudio();
      } catch (audioErr) {
        console.warn('startAudio() failed, will prompt for a tap:', audioErr);
        setAudioBlocked(true);
      }

      // Pick up any agent track that was already subscribed during connect()
      newRoom.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (publication.isSubscribed && publication.track) {
            attachAudioTrack(publication.track);
          }
        });
      });

      setRoom(newRoom);
      setConnected(true);
      setConnecting(false);

      const localAudioTrack = await createLocalAudioTrack({
        echoCancellation: true,
        noiseSuppression: true,
      });
      await newRoom.localParticipant.publishTrack(localAudioTrack);
    } catch (err) {
      console.error('Error connecting to Daily Review room:', err);
      setError(err.message || 'Unable to connect to call.');
      setConnecting(false);
    }
  };

  const disconnectFromRoom = () => {
    if (room) {
      room.removeAllListeners();
      room.disconnect();
      removeAudioElements();
      setRoom(null);
      setConnected(false);
      setIsSpeaking(false);
      setAudioBlocked(false);
    }
  };

  const formatCurrency = (val) => {
    const amount = Number(val ?? 0);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return `${currency.symbol} ${safeAmount.toLocaleString(currency.locale)}`;
  };

  return (
    <div className="dr-app-container">
      <div className="dr-phone-mockup">
        {/* Header */}
        <div className="dr-header">
          <img src={maisyLogo} alt="Maisy Logo" className="dr-logo" />
          <h1 className="dr-title">AI DAILY REVIEW</h1>
          <div className="dr-subtitle">Sales &amp; Performance Analytics</div>
        </div>

        {/* Meta pills */}
        <div className="dr-meta-row">
          {metaInfo.date && <span className="dr-meta-pill">📅 {metaInfo.date}</span>}
          {metaInfo.cmpId && <span className="dr-meta-pill">CMP: {metaInfo.cmpId}</span>}
          {(metaInfo.userName || metaInfo.empId) && (
            <span className="dr-meta-pill dr-emp-pill" title={metaInfo.empId ? `Emp ID: ${metaInfo.empId}` : undefined}>
              👤 {metaInfo.userName || metaInfo.empId}
            </span>
          )}
        </div>

        {/* Performance analytics */}
        <div className="dr-analytics">
          <div className="dr-analytics-heading">
            <span className="dr-heading-left">
              <span className="dr-live-dot"></span>
              Daily Performance
            </span>
            <span className="dr-sync-tag">Live Sync</span>
          </div>

          {infields ? (
            <>
              <div className="dr-revenue-strip">
                <div className="dr-revenue-left">
                  <span className="dr-revenue-label">Total Revenue</span>
                  <div className="dr-revenue-val">{formatCurrency(infields.order_amount_inr ?? 0)}</div>
                </div>
                <div className="dr-revenue-right">
                  <span className="dr-currency-tag">{currency.label}</span>
                  <span className="dr-products-stat">
                    Products: <strong>{infields.unique_products_sold ?? 0}</strong>
                  </span>
                </div>
              </div>

              <div className="dr-kpi-grid">
                <div className="dr-kpi-tile">
                  <div className="dr-tile-header">
                    <span className="dr-tile-label">Planned</span>
                    <span className="dr-tile-dot blue"></span>
                  </div>
                  <div className="dr-tile-number">{infields.stores_planned ?? 0}</div>
                </div>

                <div className="dr-kpi-tile">
                  <div className="dr-tile-header">
                    <span className="dr-tile-label">Visited</span>
                    <span className="dr-tile-dot emerald"></span>
                  </div>
                  <div className="dr-tile-number emerald">{infields.stores_visited ?? 0}</div>
                </div>

                <div className="dr-kpi-tile">
                  <div className="dr-tile-header">
                    <span className="dr-tile-label">Remaining</span>
                    <span className="dr-tile-dot amber"></span>
                  </div>
                  <div className={`dr-tile-number ${(infields.stores_left ?? 0) === 0 ? 'emerald' : 'amber'}`}>
                    {infields.stores_left ?? 0}
                  </div>
                </div>

                <div className="dr-kpi-tile">
                  <div className="dr-tile-header">
                    <span className="dr-tile-label">Orders</span>
                    <span className="dr-tile-dot purple"></span>
                  </div>
                  <div className="dr-tile-number purple">{infields.stores_with_orders ?? 0}</div>
                </div>
              </div>
            </>
          ) : (
            <div className="dr-empty-analytics">
              {loadingSummary
                ? '✨ Synchronizing performance records...'
                : 'Awaiting employee token verification...'}
            </div>
          )}
        </div>

        {/* Call section */}
        <div className="dr-call-section">
          <button
            onClick={connected || connecting ? disconnectFromRoom : handleStartReview}
            className={`dr-call-orb ${connecting ? 'connecting' : ''} ${!connected && !connecting ? 'start' : 'end'}`}
            disabled={connecting}
            title={connected ? 'End Voice Review' : 'Start Voice Review'}
          >
            <div className="dr-orb-icon">
              {!connected ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9C4.63 15.55 4 13.85 4 12zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1C19.37 8.45 20 10.15 20 12c0 4.42-3.58 8-8 8z"/>
                </svg>
              )}
            </div>
            <div className="dr-orb-ring"></div>
          </button>

          <div className="dr-call-status-label">
            {connecting ? 'CONNECTING...' : (!connected ? 'TAP TO START REVIEW' : 'CALL ACTIVE • SPEAK NOW')}
          </div>

          {connected && isSpeaking && (
            <div className="dr-voice-waves">
              <div className="dr-wave"></div>
              <div className="dr-wave"></div>
              <div className="dr-wave"></div>
              <div className="dr-wave"></div>
              <div className="dr-wave"></div>
            </div>
          )}

          {connected && audioBlocked && (
            <button type="button" className="dr-enable-audio-btn" onClick={enableAudioPlayback}>
              🔊 Tap to enable sound
            </button>
          )}

          {error && <div className="dr-error-text">⚠️ {error}</div>}
        </div>

        {/* Assistant */}
        <div className="dr-bot-container">
          <img src={maisyBot} alt="Maisy Assistant" className="dr-bot-image" />
        </div>
      </div>
    </div>
  );
};

export default DailyReview;
